// The boat shell's navigation, in one place (TOM-193, TOM-513). A rail above
// 1024 px, a tab bar on phone and tablet pages other than home, and a sidebar
// that slides in from the left at every width. The web-only screens live in the
// sidebar's "Boat" group. Every route stays reachable from the sidebar.
import type { ReactElement } from 'react';

import { splitBoatName } from '../../lib/boatDetails';

import {
  AlarmsTabIcon,
  AnalyzerIcon,
  CamerasTabIcon,
  DevicesTabIcon,
  DisplaysTabIcon,
  ManualsTabIcon,
  SettingsTabIcon,
  WatchTabIcon,
} from './icons';

export interface ShellEntry {
  key: string;
  label: string;
  /** Relative to the boat, '' is the boat's index. */
  path: string;
  icon: () => ReactElement;
}

/** The alarms that need attention, for the badges on the rail, the bar and the sidebar. */
export interface AlarmBadge {
  count: number;
  tone: string;
}

/** A board's page, relative to the boat (TOM-192). */
export function boardPath(slug: string): string {
  return `boards/${encodeURIComponent(slug)}`;
}

/** The web-only screens: the sidebar's "Boat" group. Each row's test id is `sidebar-<key>`. */
export const BOAT_ENTRIES: readonly ShellEntry[] = [
  {
    key: 'alarms',
    label: 'Alarms',
    path: 'alarms',
    icon: () => <AlarmsTabIcon />,
  },
  {
    key: 'watch',
    label: 'AI watch',
    path: 'watch',
    icon: () => <WatchTabIcon />,
  },
  {
    key: 'devices',
    label: 'Devices',
    path: 'devices',
    icon: () => <DevicesTabIcon />,
  },
  {
    key: 'cameras',
    label: 'Cameras',
    path: 'cameras',
    icon: () => <CamerasTabIcon />,
  },
  {
    key: 'manuals',
    label: 'Manuals',
    path: 'manuals',
    icon: () => <ManualsTabIcon />,
  },
  {
    key: 'toolkit',
    label: 'Analyzer',
    path: 'toolkit',
    icon: () => <AnalyzerIcon />,
  },
  {
    key: 'displays',
    label: 'Displays',
    path: 'displays',
    icon: () => <DisplaysTabIcon />,
  },
  {
    key: 'settings',
    label: 'Settings',
    path: 'settings',
    icon: () => <SettingsTabIcon />,
  },
];

/** The screens a Try it guest cannot use (TOM-868): the server refuses their routes. */
const GUEST_HIDDEN_ENTRIES: ReadonlySet<string> = new Set(['cameras', 'displays', 'toolkit']);

/** The sidebar's "Boat" group, without the screens a Try it guest cannot use. */
export function boatEntriesFor(guest: boolean): readonly ShellEntry[] {
  return guest ? BOAT_ENTRIES.filter((e) => !GUEST_HIDDEN_ENTRIES.has(e.key)) : BOAT_ENTRIES;
}

export function boatPath(boatId: string, path: string): string {
  return path === '' ? `/dashboard/boats/${boatId}` : `/dashboard/boats/${boatId}/${path}`;
}

/** The boat's name in a chip: the last word kept whole when the rest truncates. */
export function BoatChipName({ name }: { name: string }) {
  const parts = splitBoatName(name);
  return (
    <span className="shell-boat-chip__name" aria-hidden="true">
      <span className="boat-name-lead">{parts.lead}</span>
      {parts.suffix ? <span className="boat-name-suffix">{parts.suffix}</span> : null}
    </span>
  );
}
